import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import './FileUpload.css';

const FileUpload = () => {
  const [files, setFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const [parsedData, setParsedData] = useState([]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFiles(droppedFiles);
  };

  const handleFileInput = (e) => {
    const selectedFiles = Array.from(e.target.files);
    handleFiles(selectedFiles);
  };

  const handleFiles = (fileList) => {
    const validFiles = fileList.filter(file => {
      const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');
      const isPdf = file.name.endsWith('.pdf');
      const isCsv = file.name.endsWith('.csv');
      return isExcel || isPdf || isCsv;
    });

    if (validFiles.length === 0) {
      setUploadStatus('Please select valid Excel (.xlsx, .xls), PDF, or CSV files');
      return;
    }

    // Clear previous files and data
    setFiles([]);
    setParsedData([]);
    localStorage.removeItem('stockData');

    setFiles(validFiles);
    setUploadStatus(`${validFiles.length} file(s) selected successfully`);
    
    // Process Excel and CSV files immediately
    const excelFiles = validFiles.filter(file => 
      file.name.endsWith('.xlsx') || file.name.endsWith('.xls')
    );
    const csvFiles = validFiles.filter(file => file.name.endsWith('.csv'));
    
    if (excelFiles.length > 0) {
      // Process the first Excel file (for now, we'll handle one at a time)
      processExcelFile(excelFiles[0]);
      
      if (excelFiles.length > 1) {
        setUploadStatus(`Processing first Excel file. ${excelFiles.length - 1} additional Excel file(s) detected.`);
      }
    } else if (csvFiles.length > 0) {
      // Process the first CSV file
      processCsvFile(csvFiles[0]);
      
      if (csvFiles.length > 1) {
        setUploadStatus(`Processing first CSV file. ${csvFiles.length - 1} additional CSV file(s) detected.`);
      }
    }
    
    const pdfFiles = validFiles.filter(file => file.name.endsWith('.pdf'));
    if (pdfFiles.length > 0) {
      setUploadStatus(prev => prev + ` ${pdfFiles.length} PDF file(s) uploaded (PDF parsing not yet implemented).`);
    }
  };

  const processExcelFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);
        
        if (jsonData.length === 0) {
          setUploadStatus('Excel file appears to be empty or has no valid data');
          return;
        }
        
        // Clear existing data and store new parsed data
        localStorage.setItem('stockData', JSON.stringify(jsonData));
        
        setParsedData(jsonData);
        setUploadStatus(`Excel file processed successfully! ${jsonData.length} records found.`);
      } catch (error) {
        setUploadStatus(`Error processing Excel file: ${error.message}`);
        console.error('Excel processing error:', error);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const processCsvFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csvText = e.target.result;
        const lines = csvText.split('\n').filter(line => line.trim());
        
        if (lines.length < 2) {
          setUploadStatus('CSV file appears to be empty or has no valid data');
          return;
        }
        
        // Parse CSV manually
        const headers = lines[0].split(',').map(header => header.trim());
        const jsonData = [];
        
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(',').map(value => value.trim());
          if (values.length === headers.length) {
            const row = {};
            headers.forEach((header, index) => {
              // Try to parse numbers, otherwise keep as string
              const value = values[index];
              row[header] = isNaN(value) || value === '' ? value : parseFloat(value);
            });
            jsonData.push(row);
          }
        }
        
        if (jsonData.length === 0) {
          setUploadStatus('CSV file contains no valid data rows');
          return;
        }
        
        // Clear existing data and store new parsed data
        localStorage.setItem('stockData', JSON.stringify(jsonData));
        
        setParsedData(jsonData);
        setUploadStatus(`CSV file processed successfully! ${jsonData.length} records found.`);
      } catch (error) {
        setUploadStatus(`Error processing CSV file: ${error.message}`);
        console.error('CSV processing error:', error);
      }
    };
    reader.readAsText(file);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const clearAll = () => {
    setFiles([]);
    setParsedData([]);
    setUploadStatus('All files cleared. Upload new files to get started.');
    localStorage.removeItem('stockData');
  };

  const downloadSampleExcel = () => {
    // Create sample data
    const sampleData = [
      {
        'PRODUCT CODE': 'STD0001',
        'PRODUCT DESCRIPTION': 'AMINORICH CAP 15 C',
        'OPENING STOCK': 39,
        'OPENING VALUE': 4888.65,
        'RECEIVE QUANTITY': 0,
        'RECEIVE VALUE': 0,
        'ISSUE QUANTITY': 0,
        'ISSUE VALUE': 0,
        'CLOSING STOCK': 39,
        'CLOSING VALUE': 4888.65,
        'EXPIRY STOCK': 0,
        'Month': 'Dec',
        'Region': 'North',
        'Stockist': 'Stedman Pharma North',
        'PTS': 85.50,
        'PTR': 105.20,
        'MRP': 125.00,
        'NRV': 75.30,
        'Sales Qty': 15,
        'Primary Sales': 12500.00,
        'Secondary Sales': 11200.00,
        'Product Group': 'AMINO Group'
      },
      {
        'PRODUCT CODE': 'STD0002',
        'PRODUCT DESCRIPTION': 'DREZ POWDER 10GM',
        'OPENING STOCK': 4,
        'OPENING VALUE': 224.48,
        'RECEIVE QUANTITY': 0,
        'RECEIVE VALUE': 0,
        'ISSUE QUANTITY': 0,
        'ISSUE VALUE': 0,
        'CLOSING STOCK': 4,
        'CLOSING VALUE': 224.48,
        'EXPIRY STOCK': 0,
        'Month': 'Jan',
        'Region': 'South',
        'Stockist': 'Stedman Pharma South',
        'PTS': 45.20,
        'PTR': 55.80,
        'MRP': 65.00,
        'NRV': 40.15,
        'Sales Qty': 25,
        'Primary Sales': 8500.00,
        'Secondary Sales': 7800.00,
        'Product Group': 'DREZ Group'
      },
      {
        'PRODUCT CODE': 'STD0003',
        'PRODUCT DESCRIPTION': 'DREZ-V CREAM 30GM',
        'OPENING STOCK': 55,
        'OPENING VALUE': 657.8,
        'RECEIVE QUANTITY': 0,
        'RECEIVE VALUE': 0,
        'ISSUE QUANTITY': 5,
        'ISSUE VALUE': 701.66,
        'CLOSING STOCK': 50,
        'CLOSING VALUE': 598.0,
        'EXPIRY STOCK': 0,
        'Month': 'Dec',
        'Region': 'East',
        'Stockist': 'Stedman Pharma East',
        'PTS': 92.30,
        'PTR': 115.40,
        'MRP': 135.00,
        'NRV': 82.15,
        'Sales Qty': 35,
        'Primary Sales': 15500.00,
        'Secondary Sales': 14200.00,
        'Product Group': 'DREZ Group'
      }
    ];

    // Create workbook and worksheet
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(sampleData);
    
    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(wb, ws, 'Stock Data');
    
    // Save the file
    XLSX.writeFile(wb, 'sample-stock-data.xlsx');
    
    setUploadStatus('Sample Excel file downloaded successfully! You can now upload it to test the application.');
  };

  return (
    <div className="upload-container">
      <div className="upload-content">
        <h1 className="upload-title">Upload Your Stock Data</h1>
        <p className="upload-subtitle">
          Upload Excel files (.xlsx, .xls), CSV files (.csv), or PDF documents containing stock and sales data
        </p>
        <div className="upload-instructions">
          <h3>📋 Data Format Requirements:</h3>
          <p>Your Excel file should contain columns like:</p>
          <div className="required-columns">
            <span>PRODUCT CODE</span>
            <span>PRODUCT DESCRIPTION</span>
            <span>OPENING STOCK</span>
            <span>CLOSING STOCK</span>
            <span>OPENING VALUE</span>
            <span>CLOSING VALUE</span>
          </div>
          <p className="format-note">
            💡 Download our sample data file to see the expected format
          </p>
        </div>

        <div 
          className={`upload-dropzone ${dragActive ? 'active' : ''}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <div className="upload-icon">📁</div>
          <p className="upload-text">
            Drag & drop your files here, or{' '}
            <label className="upload-browse">
              browse
              <input
                type="file"
                multiple
                accept=".xlsx,.xls,.pdf,.csv"
                onChange={handleFileInput}
                style={{ display: 'none' }}
              />
            </label>
          </p>
          <p className="upload-formats">Supports: Excel (.xlsx, .xls), CSV (.csv), and PDF files</p>
        </div>

        {uploadStatus && (
          <div className={`upload-status ${uploadStatus.includes('Error') ? 'error' : 'success'}`}>
            {uploadStatus}
          </div>
        )}

        {files.length > 0 && (
          <div className="files-section">
            <div className="files-header">
              <h3>Uploaded Files ({files.length})</h3>
              <button className="clear-btn" onClick={clearAll}>
                Clear All
              </button>
            </div>
            <div className="files-list">
              {files.map((file, index) => (
                <div key={index} className="file-item">
                  <div className="file-info">
                    <span className="file-icon">
                      {file.name.endsWith('.pdf') ? '📄' : 
                       file.name.endsWith('.csv') ? '📋' : '📊'}
                    </span>
                    <div className="file-details">
                      <span className="file-name">{file.name}</span>
                      <span className="file-size">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                    </div>
                  </div>
                  <button 
                    className="remove-btn"
                    onClick={() => removeFile(index)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {parsedData.length > 0 && (
          <div className="preview-section">
            <h3>Data Preview</h3>
            <div className="data-preview">
              <p>{parsedData.length} records processed</p>
              <div className="preview-table">
                {parsedData.slice(0, 5).map((row, index) => (
                  <div key={index} className="preview-row">
                    {Object.entries(row).slice(0, 4).map(([key, value]) => (
                      <div key={key} className="preview-cell">
                        <strong>{key}:</strong> {value}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              {parsedData.length > 5 && (
                <p className="preview-note">
                  Showing first 5 records. Total: {parsedData.length}
                </p>
              )}
            </div>
          </div>
        )}

        <div className="upload-actions">
          <button 
            className="sample-btn"
            onClick={downloadSampleExcel}
          >
            📥 Download Sample Excel
          </button>
          <button 
            className="dashboard-btn"
            onClick={() => window.location.href = '/dashboard'}
            disabled={parsedData.length === 0}
          >
            View Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

export default FileUpload;